/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import QuillEditor from '../../../components/Quill/Quill';
import adminServices from '../../../services/adminServices';

window.katex = katex;

const Parchment = Quill.import("parchment");
const Style = new Parchment.Attributor.Style("style", "style");
Quill.register(Style, true);

const ImageWidth = new Parchment.Attributor.Attribute("width", "width", {
  scope: Parchment.Scope.INLINE,
});
Quill.register(ImageWidth, true);

const ImageHeight = new Parchment.Attributor.Attribute("height", "height", {
  scope: Parchment.Scope.INLINE,
});
Quill.register(ImageHeight, true);

const NumericAnswer = ({ formData, setFormData, errors, setErrors }) => {

  const quillRefs = {
    questionText: useRef(null),
    textExplanation: useRef(null)
  };
  useEffect(() => {
    const needsNormalisation =
      (formData.answerType != null && typeof formData.answerType !== 'string') ||
      (formData.acceptableAnswers != null && typeof formData.acceptableAnswers !== 'string');

    if (needsNormalisation) {
      setFormData(prev => ({
        ...prev,
        answerType: prev.answerType != null ? String(prev.answerType).toLowerCase() : 'exact',
        acceptableAnswers: prev.acceptableAnswers != null ? String(prev.acceptableAnswers) : '',
      }));
    }
  }, []);

  const formats = React.useMemo(() => [
    "bold", "italic", "underline",
    "list", "bullet",
    "script",
    "header",
    "image",
    "formula",
    "color", "background",
    "align", "style", "width", "height"
  ], []);

  const getModules = (targetRef) => ({
    toolbar: {
      container: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ header: [1, 2, 3, false] }],
        ["image", { formula: { customClass: 'qlformula' } }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["clean"],
      ],
      handlers: {
        image: () => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.click();

          input.onchange = () => {
            if (input.files?.length) {
              handleFiles(input.files, targetRef);
            }
          };
        },
      },
    },
    clipboard: { matchVisual: false },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    },
  });

  const questionModules = React.useMemo(() => getModules(quillRefs.questionText), []);
  const explanationModules = React.useMemo(() => getModules(quillRefs.textExplanation), []);

  const handleFiles = async (files, targetRef) => {
    const quillInstance = targetRef?.current?.getEditor();
    if (!quillInstance) return;

    for (const file of files) {
      if (file.type.startsWith("image/")) {
        const range = quillInstance.getSelection(true);
        const tempId = `temp-${Date.now()}`;

        // Insert loading placeholder
        const loadingGif = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='40' stroke='%234F46E5' stroke-width='8' fill='none' stroke-dasharray='60 200' stroke-linecap='round'%3E%3CanimateTransform attributeName='transform' type='rotate' dur='1s' repeatCount='indefinite' from='0 50 50' to='360 50 50'/%3E%3C/circle%3E%3C/svg%3E";
        quillInstance.insertEmbed(range.index, "image", loadingGif);
        quillInstance.setSelection(range.index + 1);

        const url = await handleUploadImage(file);
        if (url) {
          const editorEl = quillInstance.root;
          const imgElements = editorEl.querySelectorAll('img');
          const loadingImg = Array.from(imgElements).find(img => img.src === loadingGif);
          if (loadingImg) {
            loadingImg.setAttribute("src", url);
          }
        }
      }
    }
  };

  const handleUploadImage = async (file) => {
    const formData = new FormData();
    formData.append("thumbImage", file);
    try {
      const imageUrl = await adminServices.postImag(formData);
      if (imageUrl?.ok) {
        return imageUrl.data;
      }
    } catch (error) {
      console.error("Image upload failed", error);
    }
    return null;
  };

  React.useEffect(() => {
    const mountedRefs = Object.values(quillRefs).filter(ref => ref?.current);
    if (!mountedRefs.length) return;

    mountedRefs.forEach((ref) => {
      const editor = ref.current?.root;
      if (!editor) return;

      const pasteHandler = (e) => {
        const items = e.clipboardData?.items;
        if (items) {
          const files = [];
          for (const item of items) {
            if (item.type.indexOf("image") !== -1) {
              e.preventDefault();
              files.push(item.getAsFile());
            }
          }
          if (files.length) handleFiles(files, ref);
        }
      };

      const dropHandler = (e) => {
        e.preventDefault();
        const files = e.dataTransfer?.files;
        if (files?.length) handleFiles(files, ref);
      };

      editor.addEventListener("paste", pasteHandler);
      editor.addEventListener("drop", dropHandler);
      editor.addEventListener("dragover", (e) => e.preventDefault());

      return () => {
        editor.removeEventListener("paste", pasteHandler);
        editor.removeEventListener("drop", dropHandler);
      };
    });
  }, [quillRefs.questionText.current, quillRefs.textExplanation.current]);

  const handleQuillChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (setErrors && errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (setErrors && errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <div className="space-y-6" id="numeric-answer-form">
      {/* Question Text */}
      <div className="mb-6" id="question-text-editor">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question Text <span className="text-red-500">*</span>
        </label>
        <QuillEditor
          ref={quillRefs.questionText}
          id="quill-question-text"
          value={formData.questionText || ''}
          onChange={(value) => handleQuillChange('questionText', value)}
          theme="snow"
          modules={questionModules}
          formats={formats}
          placeholder="Enter your question here..."
        />
        {errors?.questionText && (
          <p className="mt-1 text-sm text-red-600">{errors.questionText}</p>
        )}
      </div>

      {/* Numeric Answer Section */}
      <div className="rounded-lg border border-purple-200 p-6 mt-6" id="numeric-answer-section">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Numeric Answer</h3>

        {/* Answer Type */}
        <div className="mb-6" id="answer-type-section">
          <label htmlFor="answerType" className="block text-sm font-medium text-gray-700 mb-2">
            Answer Type <span className="text-red-500">*</span>
          </label>
          <select
            id="answerType"
            name="answerType"
            value={formData.answerType || 'exact'}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white ${errors?.answerType ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
          >
            <option value="exact">Exact Values</option>
            <option value="range">Range</option>
          </select>
          {errors?.answerType && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.answerType}
            </p>
          )}
        </div>

        {/* Acceptable Answers */}
        {formData.answerType !== 'range' && (
          <div id="exact-values-section">
            <label htmlFor="acceptableAnswers" className="block text-sm font-medium text-gray-700 mb-2">
              Acceptable Answers (comma-separated) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="acceptableAnswers"
              name="acceptableAnswers"
              value={formData.acceptableAnswers || ''}
              placeholder="e.g., 3.14, -3.1416"
              onChange={(e) => {
                let value = e.target.value;
                value = value.replace(/[A-Za-z]/g, '');
                let sign = '';
                if (value.startsWith('-') || value.startsWith('+')) {
                  sign = value[0];
                  value = value.slice(1);
                }
                value = value.replace(/[+-]/g, '');
                const parts = value.split('.');
                if (parts.length > 2) {
                  value = parts.shift() + '.' + parts.join('');
                }
                value = sign + value;
                handleInputChange({
                  target: {
                    name: 'acceptableAnswers',
                    value,
                  },
                });
              }}
              onKeyDown={(e) => {
                if (/^[A-Za-z]$/.test(e.key)) {
                  e.preventDefault();
                }
                if ((e.key === '+' || e.key === '-') && e.target.selectionStart !== 0) {
                  e.preventDefault();
                }
                if ((e.key === '+' || e.key === '-') && formData.acceptableAnswers?.[0]) {
                  e.preventDefault();
                }
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white ${errors?.acceptableAnswers ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
            />
            {/* <input
            type="text"
            id="acceptableAnswers"
            name="acceptableAnswers"
            value={formData.acceptableAnswers || ''}
            onChange={handleInputChange}
            placeholder="e.g., 3.14, 3.1416"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white ${
              errors?.acceptableAnswers ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
          /> */}
            <p className="mt-1 text-xs text-gray-500">
              Enter comma-separated numeric values that are acceptable as correct answers
            </p>
            {errors?.acceptableAnswers && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.acceptableAnswers}
              </p>
            )}
          </div>
        )}
        {formData.answerType === 'range' && (
          <div className="grid grid-cols-2 gap-4" id="range-values-section">
            <div>
              <label htmlFor="minValue" className="block text-sm font-medium text-gray-700 mb-2">
                Min Value <span className="text-red-500">*</span>
              </label>
              <input type="number" id="minValue" name="min_value" value={formData.min_value || ''} onChange={handleInputChange} placeholder="e.g., 3.0"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm bg-white ${errors?.minValue ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {errors?.minValue && <p className="mt-1 text-sm text-red-600">{errors.minValue}</p>}
            </div>
            <div>
              <label htmlFor="maxValue" className="block text-sm font-medium text-gray-700 mb-2">
                Max Value <span className="text-red-500">*</span>
              </label>
              <input type="number" id="maxValue" name="max_value" value={formData.max_value || ''} onChange={handleInputChange} placeholder="e.g., 3.2"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm bg-white ${errors?.maxValue ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {errors?.maxValue && <p className="mt-1 text-sm text-red-600">{errors.maxValue}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Explanation Section */}
      <div className="rounded-lg border border-blue-200 p-6 mt-6" id="explanation-section">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Explanation</h3>

        {/* Text Explanation */}
        <div className="mb-6" id="explanation-editor">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Text Explanation <span className="text-red-500">*</span>
          </label>
          <QuillEditor
            ref={quillRefs.textExplanation}
            id="quill-text-explanation"
            value={formData.textExplanation || ''}
            onChange={(value) => handleQuillChange('textExplanation', value)}
            theme="snow"
            modules={explanationModules}
            formats={formats}
            placeholder="Explain the correct answer..."
          />
          {errors?.textExplanation && (
            <p className="mt-1 text-sm text-red-600">{errors.textExplanation}</p>
          )}
        </div>

        {/* YouTube Video URL */}
        <div>
          <label htmlFor="youtubeVideoUrl" className="block text-sm font-medium text-gray-700 mb-2">
            YouTube Video Explanation (Optional)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              id="youtubeVideoUrl"
              name="youtubeVideoUrl"
              value={formData.youtubeVideoUrl || ''}
              onChange={handleInputChange}
              placeholder="https://youtube.com/watch?v=..."
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors bg-white ${errors?.youtubeVideoUrl ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
            />
          </div>
          {errors?.youtubeVideoUrl && (
            <p className="mt-1 text-sm text-red-600 flex items-start gap-1">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.youtubeVideoUrl}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumericAnswer;